import { useEffect, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  AlertTriangle,
  Settings,
  Home,
  Briefcase,
  Wallet,
  Map as MapIcon,
  TrendingUp,
  Bell,
  LogOut,
  Plus,
  FileText,
  UserPlus,
} from "@/components/brand/ScrubmeeIcons";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ScrubmeeLogo } from "@/components/brand/ScrubmeeLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ROLE_LABELS } from "@shared/types";
import type { UserRole } from "@shared/types";
import { api } from "@/lib/api";

interface NavItem {
  label: string;
  path: string;
  icon: typeof LayoutDashboard;
}

const NAV_ITEMS: Record<UserRole, NavItem[]> = {
  admin: [
    { label: "Overview", path: "/admin", icon: LayoutDashboard },
    { label: "Jobs", path: "/admin/jobs", icon: Briefcase },
    { label: "Properties", path: "/admin/properties", icon: Home },
    { label: "KYC Review", path: "/admin/kyc", icon: ShieldCheck },
    { label: "Disputes", path: "/admin/disputes", icon: AlertTriangle },
    { label: "Payments", path: "/admin/payments", icon: Wallet },
    { label: "Users", path: "/admin/users", icon: Users },
    { label: "Agents", path: "/admin/agents", icon: UserPlus },
    { label: "Audit log", path: "/admin/audit", icon: FileText },
    { label: "Configuration", path: "/admin/config", icon: Settings },
  ],
  host: [
    { label: "Overview", path: "/host", icon: LayoutDashboard },
    { label: "Properties", path: "/host/properties", icon: Home },
    { label: "Jobs", path: "/host/jobs", icon: Briefcase },
    { label: "Payments", path: "/host/payments", icon: Wallet },
  ],
  cleaner: [
    { label: "Job Feed", path: "/cleaner", icon: MapIcon },
    { label: "My Jobs", path: "/cleaner/my-jobs", icon: Briefcase },
    { label: "KYC", path: "/cleaner/kyc", icon: ShieldCheck },
    { label: "Earnings", path: "/cleaner/earnings", icon: TrendingUp },
  ],
  field_agent: [
    { label: "Overview", path: "/agent", icon: LayoutDashboard },
    { label: "Referrals", path: "/agent/referrals", icon: Users },
  ],
};

function isNavActive(pathname: string, itemPath: string) {
  if (pathname === itemPath) return true;
  const section = `/${pathname.split("/")[1]}`;
  if (itemPath === section) return false;
  return itemPath !== "/admin" &&
    itemPath !== "/host" &&
    itemPath !== "/cleaner" &&
    itemPath !== "/agent"
    ? pathname.startsWith(itemPath)
    : false;
}

function NavLinks({
  items,
  onNavigate,
}: {
  items: NavItem[];
  onNavigate?: () => void;
}) {
  const location = useLocation();
  return (
    <nav aria-label="Main navigation">
      <ul className="flex flex-col gap-1">
        {items.map((item) => {
          const isActive = isNavActive(location.pathname, item.path);
          const Icon = item.icon;
          return (
            <li key={item.path}>
              <Link
                to={item.path}
                onClick={onNavigate}
                className={cn(
                  "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150",
                  isActive
                    ? "bg-brand text-brand-foreground"
                    : "text-background/65 hover:bg-background/10 hover:text-background",
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon
                  className="h-[18px] w-[18px] shrink-0"
                  aria-hidden="true"
                />
                <span>{item.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function UserAvatar({
  size = "default",
  className,
}: {
  size?: "default" | "sm";
  className?: string;
}) {
  const { user } = useAuth();
  if (!user) return null;
  const initials = user.full_name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <Avatar
      className={cn(size === "default" ? "h-9 w-9" : "h-8 w-8", className)}
    >
      {user.avatar_url ? (
        <AvatarImage src={user.avatar_url} alt={user.full_name} />
      ) : null}
      <AvatarFallback className="bg-brand/20 text-xs font-semibold text-brand">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

interface Notification {
  id: string;
  title: string;
  body: string;
  read_at: string | null;
  created_at: string;
  metadata: string | null;
}

function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  async function load() {
    try {
      const [countRes, listRes] = await Promise.all([
        api.get<{ data: { count: number } }>("/notifications/unread-count"),
        api.get<{ data: Notification[] }>("/notifications?limit=10"),
      ]);
      setUnread(countRes.data?.count ?? 0);
      setItems(listRes.data ?? []);
    } catch {
      /* notifications are non-critical */
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function markAllRead() {
    try {
      await api.put("/notifications/read-all");
      setUnread(0);
      setItems((prev) =>
        prev.map((n) => ({
          ...n,
          read_at: n.read_at ?? new Date().toISOString(),
        })),
      );
    } catch {
      /* ignore */
    }
  }

  function openNotification(n: Notification) {
    const link = n.metadata ? parseLink(n.metadata) : null;
    setOpen(false);
    if (link) navigate(link);
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) load();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
          className="relative"
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {unread > 0 ? (
            <span
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-brand ring-2 ring-background"
              aria-hidden="true"
            />
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-3 py-2">
          <p className="text-sm font-semibold">Notifications</p>
          {unread > 0 ? (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs font-medium text-primary hover:underline"
            >
              Mark all read
            </button>
          ) : null}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              No notifications yet.
            </p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => openNotification(n)}
                className={cn(
                  "block w-full border-t px-3 py-3 text-left",
                  n.read_at ? "opacity-70" : "bg-accent/20",
                )}
              >
                <p className="text-sm font-medium">{n.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{n.body}</p>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function parseLink(metadata: string): string | null {
  try {
    const parsed = JSON.parse(metadata) as { link?: unknown };
    return typeof parsed.link === "string" && parsed.link.startsWith("/")
      ? parsed.link
      : null;
  } catch {
    return null;
  }
}

export function AppShell(): ReactNode {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  if (!user) return null;

  const navItems = NAV_ITEMS[user.role] ?? [];
  const roleLabel = ROLE_LABELS[user.role] ?? "";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside
          className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-foreground lg:flex"
          aria-label="Sidebar navigation"
        >
          <div className="flex h-20 shrink-0 items-center px-5">
            <Link
              to="/"
              className="flex items-center transition-transform duration-200 hover:scale-[1.02]"
              aria-label="Scrubmee home"
            >
              <ScrubmeeLogo
                variant="horizontal"
                tone="light"
                className="h-14 w-auto"
              />
            </Link>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto px-3 pb-3 pt-2">
            <div className="mb-3 px-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-background/50">
                {roleLabel}
              </p>
            </div>
            <NavLinks items={navItems} />
            {user.role === "host" ? (
              <Link
                to="/host/jobs/new"
                className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-brand px-3 py-2.5 text-sm font-semibold text-brand-foreground transition-colors hover:bg-brand/90"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Post a job
              </Link>
            ) : null}
          </div>
          <div className="border-t border-white/10 p-3">
            <Link
              to="/settings"
              className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-background/10"
              aria-label="Open settings"
            >
              <UserAvatar />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-background">
                  {user.full_name.split(" ")[0]}
                </span>
                <span className="block truncate text-xs text-background/50">
                  {roleLabel}
                </span>
              </span>
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </aside>

        <div className="flex flex-1 flex-col pb-16 lg:pb-0 lg:pl-64">
          <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center justify-between border-b border-border/70 bg-background px-4 lg:px-8">
            <div className="flex items-center gap-3">
              <Link to="/" className="lg:hidden flex items-center" aria-label="Scrubmee home">
                <ScrubmeeLogo variant="horizontal" tone="dark" className="h-11 w-auto" />
              </Link>
              <p className="hidden text-sm text-muted-foreground lg:block">
                {roleLabel} dashboard
              </p>
            </div>

            <div className="flex items-center gap-1">
              <NotificationBell />
              <Link
                to="/settings"
                className="ml-1 border-l pl-3 lg:hidden"
                aria-label="Open settings"
              >
                <UserAvatar size="sm" />
              </Link>
            </div>
          </header>

          <main className="flex-1 p-4 lg:p-8" id="main-content">
            <div className="mx-auto w-full max-w-6xl">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Mobile bottombar (replaces sidebar on small screens) */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-background/10 bg-foreground lg:hidden"
        aria-label="Mobile navigation"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="mx-auto flex max-w-full items-center gap-1 overflow-x-auto px-2 py-2">
          {navItems.map((item) => {
            const isActive = isNavActive(location.pathname, item.path);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex w-20 shrink-0 flex-col items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors",
                  isActive
                    ? "text-brand"
                    : "text-background/50 hover:text-background",
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <span
                  className={cn(
                    "flex h-8 w-12 items-center justify-center rounded-full transition-colors",
                    isActive ? "bg-brand/15" : "",
                  )}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="truncate max-w-[64px]">{item.label}</span>
              </Link>
            );
          })}
          {user.role === "host" ? (
            <Link
              to="/host/jobs/new"
              className={cn(
                "flex w-20 shrink-0 flex-col items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors",
                location.pathname === "/host/jobs/new"
                  ? "text-brand"
                  : "text-background/50 hover:text-background",
              )}
            >
              <span className="flex h-8 w-12 items-center justify-center rounded-full bg-brand text-brand-foreground">
                <Plus className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="truncate max-w-[64px]">Post job</span>
            </Link>
          ) : null}
        </div>
      </nav>
    </div>
  );
}
